import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { Box, Button, Chip, Divider, Typography } from "@mui/material";
import { shape } from "@app/design-tokens";
import dayjs from "dayjs";
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { useAuth } from "@/auth/authContext";
import { setLoginNotice } from "@/auth/loginNotice";
import { PageHeader } from "@/layout/PageHeader";
import { serviceIcon } from "@/layout/serviceIcons";

import { ChangePasswordDialog } from "./ChangePasswordDialog";

/**
 * 個人中心：帳號狀態與基本資料。
 *
 * **資料整頁唯讀**，而且這不是偷懶：`/me/` 的序列化器把所有欄位都設成
 * read_only，沒有 PATCH 端點。基本資料（姓名、員工編號、部門、電話）與
 * 權限一律由管理員在 Django Admin 維護，所以這裡放編輯欄位會是騙人的。
 * 頁面直接把這件事寫出來，使用者才知道該找誰改。
 *
 * 唯一使用者自己能改的是密碼：「修改密碼」放在右下角、登出的旁邊，點了
 * 開對話框（`ChangePasswordDialog`），不是畫在卡片裡的欄位 —— 它會把所有
 * 裝置登出，要有一個明確打開、明確送出的動作。`POST /me/logout-all/`
 * （登出所有裝置）後端已有，這一頁還沒放。
 *
 * 登出（只登出這個裝置）放在這一頁的右下角，右上角的帳號選單裡沒有。
 */

/** 一列「標籤：值」。值沒有資料時顯示破折號，不要顯示空白 —— 空白看起來像壞了 */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.25, sm: 2 },
        py: 1.25,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ width: { sm: 132 }, flex: "none" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0, wordBreak: "break-word" }}>
        {children || "—"}
      </Typography>
    </Box>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: `${shape.large}px`,
        p: 2.5,
      }}
    >
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Divider sx={{ mt: 1.5 }} />
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

/** 權限層級。superuser 與 staff 是後端兩件不同的事，不要合併成一句 */
function accessLevel(isSuperuser: boolean, isStaff: boolean): string {
  if (isSuperuser) {
    return "超級使用者（所有服務、所有權限）";
  }
  return isStaff ? "後台人員（可登入 Django Admin）" : "一般使用者";
}

export function AccountPage() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const services = me?.services ?? [];
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = async () => {
    await logout();
    void navigate("/login", { replace: true });
  };

  // 後端已經作廢了所有 refresh token（包含這一台），這裡只是清本地狀態，
  // 把「為什麼被登出」帶到登入頁顯示，不然看起來像當機被踢出去。
  const handlePasswordChanged = async () => {
    setChangingPassword(false);
    setLoginNotice("密碼已更新，請用新密碼重新登入。");
    await logout();
    void navigate("/login", { replace: true });
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: "個人中心" }]}
        title="個人中心"
        description="您的帳號狀態、基本資料，以及在各服務中的角色。"
      />

      {/*
        滾動區用 flex 欄，讓登出那一列用 mt: auto 貼在底部：內容短時在畫面
        右下角，內容長時接在最後一張卡片下面，不會蓋住卡片（手機底部還有懸浮列）。
      */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "auto",
          px: 3,
          pb: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" },
            alignItems: "start",
          }}
        >
          <Card title="帳號狀態">
            <Row label="Email">{me?.email}</Row>
            <Row label="手機號碼">{me?.mobile}</Row>
            <Row label="權限層級">{me ? accessLevel(me.is_superuser, me.is_staff) : ""}</Row>
            <Row label="加入時間">
              {me?.date_joined ? dayjs(me.date_joined).format("YYYY-MM-DD HH:mm") : ""}
            </Row>
            <Row label="最後登入">
              {me?.last_login ? dayjs(me.last_login).format("YYYY-MM-DD HH:mm") : "尚無紀錄"}
            </Row>
          </Card>

          <Card title="基本資料">
            <Row label="姓名">{me?.full_name}</Row>
            <Row label="員工編號">{me?.employee_id}</Row>
            <Row label="部門">{me?.department}</Row>
            <Row label="聯絡電話">{me?.phone}</Row>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              基本資料由系統管理員在後台維護，需要更正請聯絡管理員。
            </Typography>
          </Card>

          <Box sx={{ gridColumn: { lg: "1 / -1" } }}>
            <Card title={`服務與角色（${services.length}）`}>
              {services.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1.25 }}>
                  您尚未被授予任何服務權限，請聯絡系統管理員。
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {services.map((service) => {
                    const Icon = serviceIcon(service.code);
                    return (
                      <Box
                        key={service.code}
                        component={Link}
                        to={`/${service.code}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          py: 1.25,
                          px: 1,
                          mx: -1,
                          borderRadius: `${shape.medium}px`,
                          color: "text.primary",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Icon fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
                        <Typography variant="subtitle2" sx={{ minWidth: 0 }}>
                          {service.name}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                          {service.roles.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              size="small"
                              sx={{ height: 22, borderRadius: `${shape.full}px` }}
                            />
                          ))}
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flex: "none", fontVariantNumeric: "tabular-nums" }}
                        >
                          {service.permissions.length} 項權限
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>
          </Box>
        </Box>

        <Box sx={{ mt: "auto", pt: 3, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LockResetOutlinedIcon />}
            onClick={() => setChangingPassword(true)}
          >
            修改密碼
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogoutOutlinedIcon />}
            onClick={() => void handleLogout()}
          >
            登出
          </Button>
        </Box>
      </Box>

      <ChangePasswordDialog
        open={changingPassword}
        onClose={() => setChangingPassword(false)}
        onChanged={() => void handlePasswordChanged()}
      />
    </>
  );
}
